import * as React from "react";
import NoDataComponent from "../Utils/NoData";
import PaginationTable from "../Pagination Tables/PaginationLeaderBoard";
import Loading from "../Utils/Loading";
import { db } from "../../FirebaseConfig";
import { UserAuth } from "../../context/AuthContext";

interface LeaderboardEntry {
  rank: number;
  username: string;
  submissionCount: number;
  total_score: number;
}

export default function UserTable() {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [points, setPoints] = React.useState<number>(0);
  const [rank, setRank] = React.useState<number>(0);
  const [leaderboard, setLeaderboard] = React.useState<LeaderboardEntry[]>([]);
  const [totalChallengesSolved, setTotalChallengesSolved] =
    React.useState<number>(0);
  const { userData } = UserAuth();

  React.useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        // Fetch all submissions to calculate the total challenges solved and total points for all users
        const allSubmissionsSnapshot = await db
          .collection("ChallengeSubmissions")
          .get();

        // Map to hold user points and submission counts
        const userPointsMap: Record<
          string,
          { submissionCount: number; totalPoints: number }
        > = {};

        allSubmissionsSnapshot.forEach((doc) => {
          const data = doc.data();
          const userid = data.userid;
          const points = parseInt(data.points, 10) || 0;

          if (!userPointsMap[userid]) {
            userPointsMap[userid] = {
              submissionCount: 0,
              totalPoints: 0,
            };
          }

          userPointsMap[userid].submissionCount += 1;
          userPointsMap[userid].totalPoints += points;
        });

        // Fetch usernames from the users collection
        const userIds = Object.keys(userPointsMap);
        const usersSnapshot = await db
          .collection("users")
          .where("id", "in", userIds)
          .get();
        const userMap: Record<string, string> = {};

        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          userMap[data.id] = data.name || "Unknown";
        });

        // Convert userPointsMap to an array and sort by total points
        const sortedLeaderboard = Object.keys(userPointsMap)
          .map((userid) => ({
            userid,
            submissionCount: userPointsMap[userid].submissionCount,
            totalPoints: userPointsMap[userid].totalPoints,
            username: userMap[userid] || "Unknown",
          }))
          .sort((a, b) => b.totalPoints - a.totalPoints);

        // Set the leaderboard with ranks
        const leaderboardData: LeaderboardEntry[] = sortedLeaderboard.map(
          (user, index) => ({
            rank: index + 1,
            username: user.username,
            submissionCount: user.submissionCount,
            total_score: user.totalPoints,
          })
        );

        setLeaderboard(leaderboardData);

        // Find the current user's rank and details
        const currentUser = sortedLeaderboard.find(
          (user) => user.userid === userData?.id
        );
        if (currentUser) {
          setRank(
            sortedLeaderboard.findIndex(
              (user) => user.userid === userData?.id
            ) + 1
          );
          setPoints(currentUser.totalPoints);
          setTotalChallengesSolved(currentUser.submissionCount);
        }

        setLoading(false); // Set loading to false after data is fetched
      } catch (err) {
        console.error("Error fetching leaderboard data:", err);
        setLoading(false); // Set loading to false in case of error
      }
    };

    if (userData?.id) {
      getData();
    }
  }, [userData]);

  return loading ? (
    <Loading />
  ) : (
    <React.Fragment>
      {leaderboard && leaderboard.length > 0 ? (
        <PaginationTable
          data={leaderboard}
          rowsPerPage={50}
          currentUserRank={rank}
        />
      ) : (
        <NoDataComponent />
      )}
    </React.Fragment>
  );
}
